import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import DatePicker from "@via/components/date-picker/date-picker.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { useGetOrganizationInvoices } from "@via/pages/billing/billing.api.tsx";
import InvoicesBanner from "@via/pages/organization/invoices/invoices-banner.tsx";
import NoInvoicesDisclaimer from "@via/pages/organization/invoices/no-invoices-disclaimer.tsx";
import {
  ORGANIZATIONS_INVOICES_TABLE,
  ORGANIZATIONS_INVOICES_TABLE_ACTIONS,
  ROUTES,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { InvoiceItem, InvoiceLineItem } from "common";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import { useParams } from "react-router-dom";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";

const columnHelper = createColumnHelper<InvoiceItem>();

const OrganizationInvoices: React.FC = () => {
  const { organizationId } = useParams();
  const [dateRange, setDateRange] = useState<DateValueType | undefined>();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data: invoiceData, isPending: isInvoicesLoading } =
    useGetOrganizationInvoices({
      startDate: dateRange?.startDate
        ? dayjs(dateRange.startDate).utc().startOf("day").toISOString()
        : undefined,
      endDate: dateRange?.endDate
        ? dayjs(dateRange.endDate).utc().endOf("day").toISOString()
        : undefined,
    });
  const invoices = invoiceData?.invoices || [];

  // Function to convert invoices to table data (only main invoices, no line items as separate rows)
  const tableData = useMemo(() => {
    return invoices.map(invoice => ({
      id: invoice.id,
      type: "invoice" as const,
      description: invoice.description,
      amount: invoice.amount,
      date: invoice.date,
      status: invoice.status,
      invoicePdfUrl: invoice.invoicePdfUrl,
      hostedInvoiceUrl: invoice.hostedInvoiceUrl,
      isExpanded: expandedRows.has(invoice.id),
      billingReason: invoice.billingReason,
      items: invoice.items, // Include items for expansion
    }));
  }, [invoices, expandedRows]);

  const handleContextMenuAction = (
    optionName: string,
    invoiceRow: InvoiceItem
  ): void => {
    switch (optionName) {
      case ORGANIZATIONS_INVOICES_TABLE_ACTIONS.DOWNLOAD:
        if (invoiceRow.invoicePdfUrl) {
          window.open(
            invoiceRow.invoicePdfUrl,
            "_blank",
            "noopener,noreferrer"
          );
        }
        break;
      case ORGANIZATIONS_INVOICES_TABLE_ACTIONS.VIEW_ON_EXTERNAL:
        if (invoiceRow.hostedInvoiceUrl) {
          window.open(
            invoiceRow.hostedInvoiceUrl,
            "_blank",
            "noopener,noreferrer"
          );
        }
        break;
    }
  };

  const toggleRowExpansion = (invoiceId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(invoiceId)) {
      newExpandedRows.delete(invoiceId);
    } else {
      newExpandedRows.add(invoiceId);
    }
    setExpandedRows(newExpandedRows);
  };

  // Status formatting utility
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      paid: { label: "Paid", className: "bg-green-100 text-green-800" },
      open: { label: "Open", className: "bg-blue-100 text-blue-800" },
      draft: { label: "Draft", className: "bg-gray-100 text-gray-800" },
      uncollectible: {
        label: "Uncollectible",
        className: "bg-red-100 text-red-800",
      },
      void: { label: "Void", className: "bg-gray-100 text-gray-600" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      className: "bg-gray-100 text-gray-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const columns = [
    columnHelper.display({
      id: "expand",
      cell: ({ row }) => {
        const rowData = row.original;

        // Only show expand button if there are line items
        if (
          !rowData.items ||
          rowData.items.length === 0 ||
          (rowData.billingReason !== "manual" &&
            !(
              rowData.billingReason === "subscription_cycle" &&
              rowData.items.length > 1
            ))
        ) {
          return <div className="w-8" />; // Empty space to maintain alignment
        }

        return (
          <button
            onClick={e => {
              e.stopPropagation();
              toggleRowExpansion(rowData.id);
            }}
            className="p-1 hover:bg-gray-100 rounded">
            {expandedRows.has(rowData.id) ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
          </button>
        );
      },
      header: () => null,
      size: 40,
      enableSorting: false,
    }),
    columnHelper.accessor("description", {
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div>
            <div className="font-medium">{rowData.description}</div>
            {expandedRows.has(rowData.id) && rowData.items && (
              <div className="mt-3 border-t pt-3">
                <div className="space-y-2">
                  {rowData.items.map((item: InvoiceLineItem, index: number) => (
                    <div
                      key={item.id || index}
                      className="bg-gray-100 p-3 rounded-md border border-gray-200">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="font-medium text-gray-900">
                          {item.description}
                        </div>
                        <div className="font-medium text-gray-900">
                          {item.amount}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
      header: () => <span>Description</span>,
    }),
    columnHelper.accessor("amount", {
      cell: ({ row }) => {
        const rowData = row.original;
        return <div className="font-medium">{rowData.amount}</div>;
      },
      header: () => <span>Amount</span>,
      meta: {
        className: "align-top",
        style: { verticalAlign: "top" },
      },
    }),
    columnHelper.accessor("status", {
      cell: ({ row }) => {
        const rowData = row.original;
        return rowData.status ? getStatusBadge(rowData.status) : null;
      },
      header: () => <span>Status</span>,
      meta: {
        className: "align-top",
        style: { verticalAlign: "top" },
      },
    }),
    columnHelper.accessor("date", {
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div>
            {rowData.date ? formatDateString(rowData.date, "LL") : null}
          </div>
        );
      },
      header: () => <span>Date</span>,
      meta: {
        className: "align-top",
        style: { verticalAlign: "top" },
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => "Invoice Actions",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div className="pl-10">
            <TableContextMenu
              options={ORGANIZATIONS_INVOICES_TABLE}
              onSelect={option => {
                handleContextMenuAction(option.name, rowData);
              }}
            />
          </div>
        );
      },
      enableSorting: false,
      meta: {
        className: "align-top",
        style: { verticalAlign: "top" },
      },
    }),
  ] as Array<ColumnDef<InvoiceItem>>;

  const getInvoicesContext = useCallback(() => {
    // Calculate summary statistics
    const totalAmount = invoices.reduce((sum, invoice) => {
      const amount = parseFloat(
        invoice.amount?.toString().replace(/[^0-9.-]+/g, "") || "0"
      );
      return sum + amount;
    }, 0);

    const statusCounts = invoices.reduce(
      (acc, invoice) => {
        const status = invoice.status || "unknown";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return {
      organizationId,
      dateRange: {
        startDate: dateRange?.startDate
          ? dayjs(dateRange.startDate).format("YYYY-MM-DD")
          : null,
        endDate: dateRange?.endDate
          ? dayjs(dateRange.endDate).format("YYYY-MM-DD")
          : null,
      },
      summary: {
        totalInvoices: invoices.length,
        totalAmount,
        statusCounts,
      },
      invoices: invoices.slice(0, 50).map(invoice => ({
        id: invoice.id,
        description: invoice.description,
        amount: invoice.amount,
        date: invoice.date,
        status: invoice.status,
        billingReason: invoice.billingReason,
        hasItems: !!invoice.items && invoice.items.length > 0,
        itemCount: invoice.items?.length || 0,
        items:
          invoice.items?.slice(0, 10).map(item => ({
            id: item.id,
            description: item.description,
            amount: item.amount,
          })) || [],
      })),
      hasMoreInvoices: invoices.length > 50,
    };
  }, [organizationId, invoices, dateRange]);

  useRegisterCortexContext({
    routePattern: ROUTES.ORGANIZATION_INVOICES,
    contextName: "Organization Invoices",
    getContext: getInvoicesContext,
    systemPrompt:
      "You are analyzing organization invoices page. Help answer questions about invoices, billing amounts, invoice statuses, date ranges, and invoice line items.",
    enabled: !!organizationId,
  });

  return (
    <div>
      {isInvoicesLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : (
        <div>
          <InvoicesBanner />
          <div className="mt-2 w-[300px]">
            <DatePicker
              placeholder="Filter By Date"
              popoverDirection="down"
              value={dateRange}
              onChange={value => {
                // This component only uses range mode (asSingle not set), so value is always DateValueType
                if (typeof value !== "string") {
                  setDateRange(value ?? undefined);
                }
              }}
            />
          </div>
          {invoices.length ? (
            <div className="invoice-table-container">
              <style>
                {`
                .invoice-table-container table td {
                  vertical-align: top !important;
                }
                .invoice-table-container table tr {
                  vertical-align: top !important;
                }
                `}
              </style>
              <Table
                columns={columns}
                data={tableData}
                total={invoices.length}
                size={TableSize.Big}
              />
            </div>
          ) : (
            <NoInvoicesDisclaimer />
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationInvoices;
